import { motion } from 'framer-motion';
import { CURRICULUM_MODULES } from '@/constants';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Download } from 'lucide-react';

export default function Curriculum() {
  return (
    <div className="bg-white" data-testid="curriculum-page">
      <section className="py-24 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl sm:text-6xl font-unbounded font-black mb-8"
            data-testid="curriculum-title"
          >
            Curriculum
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl font-manrope max-w-4xl"
          >
            A hands-on, project-driven curriculum designed to take you from prompts to production-ready AI products.
          </motion.p>
        </div>
      </section>

      <section className="py-24" data-testid="modules-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-unbounded font-black mb-12">4-Week Breakdown</h2>
          <Accordion type="single" collapsible className="space-y-4" data-testid="curriculum-accordion">
            {CURRICULUM_MODULES.map((module) => (
              <AccordionItem key={module.id} value={module.id} className="border border-black/10 bg-white px-6">
                <AccordionTrigger className="text-left font-unbounded font-bold text-xl hover:text-accent">
                  {module.title}
                </AccordionTrigger>
                <AccordionContent className="font-manrope text-gray-700 text-lg pt-4">
                  {module.description}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-24 bg-gray-50" data-testid="detailed-topics-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-unbounded font-black mb-12">Core Topics</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white border-l-4 border-accent p-8">
              <h3 className="text-2xl font-unbounded font-bold mb-4">Prompting & Agents</h3>
              <ul className="space-y-2 font-manrope text-gray-700">
                <li>• Effective prompt engineering techniques</li>
                <li>• Building autonomous AI agents</li>
                <li>• Multi-agent systems and coordination</li>
                <li>• Context management and memory</li>
              </ul>
            </div>
            <div className="bg-white border-l-4 border-accent p-8">
              <h3 className="text-2xl font-unbounded font-bold mb-4">APIs & Integrations</h3>
              <ul className="space-y-2 font-manrope text-gray-700">
                <li>• OpenAI, Anthropic, and other APIs</li>
                <li>• Building custom API wrappers</li>
                <li>• Webhook automation and triggers</li>
                <li>• Third-party tool integration</li>
              </ul>
            </div>
            <div className="bg-white border-l-4 border-accent p-8">
              <h3 className="text-2xl font-unbounded font-bold mb-4">Automation & Workflows</h3>
              <ul className="space-y-2 font-manrope text-gray-700">
                <li>• No-code/low-code automation tools</li>
                <li>• Custom workflow builders</li>
                <li>• Email and CRM automation</li>
                <li>• Data pipeline design</li>
              </ul>
            </div>
            <div className="bg-white border-l-4 border-accent p-8">
              <h3 className="text-2xl font-unbounded font-bold mb-4">Product & GTM</h3>
              <ul className="space-y-2 font-manrope text-gray-700">
                <li>• MVP development strategies</li>
                <li>• User research and testing</li>
                <li>• Pricing and positioning</li>
                <li>• Demo day preparation</li>
              </ul>
            </div>
            <div className="bg-white border-l-4 border-accent p-8">
              <h3 className="text-2xl font-unbounded font-bold mb-4">Ethical AI & Safety</h3>
              <ul className="space-y-2 font-manrope text-gray-700">
                <li>• Bias detection and mitigation</li>
                <li>• Privacy and data security</li>
                <li>• Transparency and explainability</li>
                <li>• Regulatory compliance basics</li>
              </ul>
            </div>
            <div className="bg-white border-l-4 border-accent p-8">
              <h3 className="text-2xl font-unbounded font-bold mb-4">Demo Day Prep</h3>
              <ul className="space-y-2 font-manrope text-gray-700">
                <li>• Storytelling and pitching</li>
                <li>• Live product demonstrations</li>
                <li>• Handling Q&A and objections</li>
                <li>• Follow-up and next steps</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24" data-testid="download-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-unbounded font-black mb-8">Download Full Curriculum</h2>
          <p className="text-lg font-manrope text-gray-700 mb-8">
            Get a detailed PDF breakdown of our 4-week curriculum, including daily schedules, project milestones, and mentor touchpoints.
          </p>
          <button
            data-testid="download-curriculum-button"
            className="bg-accent text-white px-10 py-5 font-bold uppercase tracking-wide hover:bg-accent-hover hover:shadow-electric transition-all inline-flex items-center gap-2"
          >
            <Download size={20} />
            Download PDF (Coming Soon)
          </button>
        </div>
      </section>
    </div>
  );
}
